import { radarI18nDe, type RadarI18n } from './de'
import radarI18nEn from './en'

export { radarI18nDe, radarI18nEn }
export type { RadarI18n }

// Export formatters
export {
  pluralize,
  formatDate,
  formatTime,
  formatDateTime,
  formatRelativeTime,
  formatTimeAgo,
  formatNumber,
  formatPercent,
  formatCurrency,
  formatDuration,
  isRTLLocale,
  getTextDirection,
} from './formatters'

// Available locales
export const availableLocales = ['de', 'en'] as const
export type Locale = (typeof availableLocales)[number]

// Default locale
export const defaultLocale: Locale = 'de'

// Translation dictionary
const translations: Record<Locale, RadarI18n> = {
  de: radarI18nDe,
  en: radarI18nEn,
}

/**
 * Get translations for a locale
 */
export function getTranslations(locale: Locale): RadarI18n {
  return translations[locale] ?? translations[defaultLocale]
}

/**
 * Detect browser locale
 * Falls back to default if not supported
 */
export function detectLocale(): Locale {
  const browserLang = navigator.language.toLowerCase()

  if (browserLang.startsWith('de')) return 'de'
  if (browserLang.startsWith('en')) return 'en'

  return defaultLocale
}

/**
 * Format hours text
 * Example: 2h, 48h
 */
export function formatHours(hours: number, locale: Locale = defaultLocale): string {
  const t = getTranslations(locale)
  return `${hours}${t.legend.hoursShort}`
}

/**
 * Format days text
 * Example: 14 Tage (de) / 14 days (en)
 */
export function formatDays(days: number, locale: Locale = defaultLocale): string {
  const t = getTranslations(locale)
  return `${days} ${t.daysSuffix}`
}

/**
 * Get status label
 */
export function getStatusLabel(status: string, locale: Locale = defaultLocale): string {
  const t = getTranslations(locale)
  const normalizedStatus = status.toLowerCase()

  switch (normalizedStatus) {
    case 'open':
      return t.status.open
    case 'accepted':
      return t.status.accepted
    case 'declined':
      return t.status.declined
    case 'filled':
      return t.status.filled
    case 'booked':
      return t.status.booked
    case 'pending':
      return t.status.pending
    default:
      return status
  }
}
